import { useEffect, useState, useRef } from 'react';
import { useApp } from '@/contexts/AppContext';
import { apiGet } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DashboardLoading } from '@/components/LoadingStates';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Users, 
  Plane, 
  UserCheck, 
  UserX, 
  AlertTriangle,
  CheckCircle,
  DollarSign,
  TrendingUp,
  Globe,
  BarChart3,
  Activity,
  Zap,
  X,
  MapPin,
  Clock,
  Calendar,
  ArrowRight,
  PlaneTakeoff,
  PlaneLanding,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WorkerPhoto } from '../hooks/useWorkerPhoto';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 100 } }
};

// Realistic 3D Globe Component
const Globe3D = () => {
  const globeImg = "https://static.prod-images.emergentagent.com/jobs/adcc3648-9eaa-4744-9c56-fefd38ab0ad0/images/c5e074ed470e957ea9887694fac940cf79b61e1d80ebed752ba09a81070ee196.png";
  
  return (
    <motion.div 
      className="relative w-full h-full flex items-center justify-center"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Glow effect */}
      <div className="absolute w-40 h-40 bg-cyan-500/30 rounded-full blur-3xl" />
      
      {/* Globe image with rotation */}
      <motion.div
        className="relative w-40 h-40"
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      >
        <img 
          src={globeImg} 
          alt="Earth Globe" 
          className="w-full h-full object-contain drop-shadow-[0_0_30px_rgba(34,211,238,0.5)]"
        />
      </motion.div>
      
      {/* Orbit rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div
          className="absolute w-52 h-52 border border-cyan-500/20 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute w-60 h-60 border border-cyan-500/10 rounded-full"
          animate={{ rotate: -360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        />
      </div>
    </motion.div>
  );
};

// Animated Line Chart Component - Multi-line like mockup
const AnimatedLineChart = ({ height = 120 }) => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => setProgress(1), 300);
    return () => clearTimeout(timer);
  }, []);
  
  // Two lines like in mockup
  const line1 = [30, 45, 35, 60, 40, 70, 50, 65, 55, 80, 60, 75];
  const line2 = [20, 35, 45, 30, 55, 40, 60, 45, 70, 55, 85, 65];
  const maxVal = 100;
  
  const createPath = (data) => {
    return data.map((val, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = height - (val / maxVal) * (height - 20);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
  };
  
  const createArea = (data) => {
    const path = createPath(data);
    return path + ` L 100 ${height} L 0 ${height} Z`;
  };
  
  return (
    <svg viewBox={`0 0 100 ${height}`} className="w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="gradient-cyan" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="gradient-amber" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.02" />
        </linearGradient>
        <filter id="glow-cyan">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Grid lines */}
      {[0, 25, 50, 75, 100].map((y, i) => (
        <line key={i} x1="0" y1={height - (y / 100) * (height - 20)} x2="100" y2={height - (y / 100) * (height - 20)} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
      ))}
      
      {/* Area fills */}
      <path
        d={createArea(line1)}
        fill="url(#gradient-cyan)"
        style={{ opacity: progress, transition: 'opacity 1s ease-out' }}
      />
      <path
        d={createArea(line2)}
        fill="url(#gradient-amber)"
        style={{ opacity: progress, transition: 'opacity 1s ease-out' }}
      />
      
      {/* Lines */}
      <path
        d={createPath(line1)}
        fill="none"
        stroke="#22d3ee"
        strokeWidth="2"
        filter="url(#glow-cyan)"
        style={{
          strokeDasharray: 500,
          strokeDashoffset: 500 * (1 - progress),
          transition: 'stroke-dashoffset 2s ease-out'
        }}
      />
      <path
        d={createPath(line2)}
        fill="none"
        stroke="#f59e0b"
        strokeWidth="2"
        style={{
          strokeDasharray: 500,
          strokeDashoffset: 500 * (1 - progress),
          transition: 'stroke-dashoffset 2s ease-out',
          transitionDelay: '0.3s'
        }}
      />
    </svg>
  );
};

// Animated Bar Chart - like mockup
const AnimatedBarChart = ({ color = "#22d3ee" }) => {
  const [animated, setAnimated] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 300);
    return () => clearTimeout(timer);
  }, []);
  
  const bars = [30, 50, 40, 70, 45, 80, 55, 90, 60, 75, 50, 85, 40, 65, 55];
  const maxVal = Math.max(...bars);
  
  return (
    <div className="flex items-end gap-[3px] h-20">
      {bars.map((val, i) => (
        <div
          key={i}
          className="flex-1 rounded-t-sm transition-all duration-700 ease-out"
          style={{
            height: animated ? `${(val / maxVal) * 100}%` : '0%',
            background: `linear-gradient(to top, ${color}cc, ${color}66)`,
            transitionDelay: `${i * 30}ms`,
            boxShadow: `0 0 8px ${color}44`
          }}
        />
      ))}
    </div>
  );
};

// Donut Chart Component - like mockup with 500%
const DonutChart = ({ percentage, color, size = 100, label }) => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => setProgress(Math.min(percentage, 100)), 500);
    return () => clearTimeout(timer);
  }, [percentage]);
  
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: 'stroke-dashoffset 1.5s ease-out',
            filter: `drop-shadow(0 0 8px ${color})`
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black text-white">{percentage}%</span>
        {label && <span className="text-[10px] text-slate-500 uppercase">{label}</span>}
      </div>
    </div>
  );
};

// Stat Card with Hex Color Style (like mockup #F97316)
const HexStatCard = ({ hexColor, title, subtitle, value, icon: Icon, trend }) => (
  <motion.div variants={itemVariants} whileHover={{ scale: 1.02, y: -3 }}>
    <Card className="relative bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden group h-full">
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: hexColor }} />
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `linear-gradient(135deg, ${hexColor}15, transparent)` }}
      />
      <CardContent className="p-5">
        <div className="flex justify-between items-start mb-3">
          <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">{title}</span>
          {Icon && (
            <div className="p-2 rounded-lg" style={{ background: `${hexColor}20` }}>
              <Icon className="h-4 w-4" style={{ color: hexColor }} />
            </div>
          )}
        </div>
        <div className="text-3xl font-black mb-1" style={{ color: hexColor }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        <div className="text-slate-400 text-sm">{subtitle}</div>
        {trend && (
          <div className="flex items-center gap-1 mt-2">
            <BarChart3 className="h-3 w-3" style={{ color: hexColor }} />
            <span className="text-xs" style={{ color: hexColor }}>{trend}</span>
          </div>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

// Toggle/Switch Card like mockup's "Feedback" section
const ToggleCard = ({ title, options }) => (
  <motion.div variants={itemVariants}>
    <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden h-full">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-600 to-slate-700" />
      <CardContent className="p-5">
        <div className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-4">{title}</div>
        <div className="flex items-center justify-between gap-4">
          {options.map((opt, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${opt.active ? 'bg-cyan-500/20' : 'bg-slate-800/50'}`}>
                <opt.icon className={`h-5 w-5 ${opt.active ? 'text-cyan-400' : 'text-slate-500'}`} />
              </div>
              <span className={`text-xs ${opt.active ? 'text-cyan-400' : 'text-slate-500'}`}>{opt.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 h-1 bg-slate-800 rounded-full overflow-hidden">
          <div className="h-full w-2/3 bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full" />
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

// Details Modal Component
const DetailsModal = ({ isOpen, onClose, title, titleAr, cases, color, icon: Icon }) => {
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900/95 backdrop-blur-2xl border border-white/10 rounded-2xl max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader className="border-b border-white/10 pb-4">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}30` }}>
              <Icon className="h-5 w-5" style={{ color: color }} />
            </div>
            <div>
              <span className="text-white font-bold text-xl">{title}</span>
              <span className="text-slate-500 text-sm block">{titleAr}</span>
            </div>
            <span className="ml-auto px-3 py-1 rounded-full text-lg font-bold" style={{ backgroundColor: `${color}20`, color: color }}>
              {cases?.length || 0}
            </span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[60vh] py-4 space-y-3">
          {cases?.length === 0 ? (
            <div className="text-center py-10">
              <Icon className="h-16 w-16 mx-auto mb-4" style={{ color: `${color}40` }} />
              <p className="text-slate-500">No records found</p>
            </div>
          ) : (
            cases?.map((c, i) => (
              <motion.div
                key={c.id || i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all"
              >
                {/* Photo */}
                <div 
                  className="w-14 h-14 rounded-xl border-2 overflow-hidden flex-shrink-0"
                  style={{ borderColor: `${color}50`, backgroundColor: `${color}20` }}
                >
                  <WorkerPhoto
                    worker={c}
                    alt={c.workerName}
                    className="w-full h-full object-cover"
                    fallback={
                      <div className="w-full h-full flex items-center justify-center text-2xl">
                        {(c.nationality || '').toUpperCase().includes('PHILIPP') ? '🇵🇭' :
                         (c.nationality || '').toUpperCase().includes('INDO') ? '🇮🇩' :
                         (c.nationality || '').toUpperCase().includes('EGYPT') ? '🇪🇬' :
                         (c.nationality || '').toUpperCase().includes('INDIA') ? '🇮🇳' : '👤'}
                      </div>
                    }
                  />
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-white truncate">{c.workerName}</h4>
                  <div className="flex items-center gap-3 mt-1 text-sm">
                    <span className="text-slate-400">🛂 {c.passportNumber || 'N/A'}</span>
                    <span className="text-slate-400">🌍 {c.nationality || 'Unknown'}</span>
                  </div>
                  {c.supplierName && (
                    <p className="text-xs text-slate-500 mt-1">Supplier: {c.supplierName}</p>
                  )}
                </div>
                
                {/* Status Badge */}
                <div 
                  className="px-3 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: `${color}20`, color: color }}
                >
                  {c.status || c.arrivalStatus || 'Pending'}
                </div>
              </motion.div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Dynamic Status Card - Shows actual workers with photos (Clickable)
const DynamicStatusCard = ({ title, titleAr, cases, color, icon: Icon, emptyMessage, bgGradient, onOpenModal }) => {
  const displayCases = cases?.slice(0, 4) || [];
  const count = cases?.length || 0;
  
  return (
    <motion.div variants={itemVariants}>
      <Card 
        className={`bg-gradient-to-br ${bgGradient} backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden h-full min-h-[180px] hover:border-white/20 transition-all duration-300 hover:shadow-[0_0_30px_-5px_${color}40] cursor-pointer group`}
        onClick={() => onOpenModal && onOpenModal({ title, titleAr, cases, color, icon: Icon })}
      >
        <div className={`absolute top-0 left-0 right-0 h-1`} style={{ background: `linear-gradient(90deg, ${color}, ${color}80)` }} />
        <CardContent className="p-5 h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}30` }}>
                <Icon className="h-4 w-4" style={{ color: color }} />
              </div>
              <div>
                <span className="text-white font-semibold text-sm">{title}</span>
                <span className="text-slate-500 text-xs block">{titleAr}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 rounded-full" style={{ backgroundColor: `${color}20` }}>
              <span className="text-xl font-black" style={{ color: color }}>{count}</span>
            </div>
          </div>
          
          {/* Content */}
          {count === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-slate-600 text-xs text-center">{emptyMessage}</p>
            </div>
          ) : (
            <div className="flex-1">
              {/* Worker Photos Row */}
              <div className="flex items-center gap-1 mb-2">
                {displayCases.map((c, i) => (
                  <div 
                    key={c.id || i} 
                    className="relative"
                    style={{ marginLeft: i > 0 ? '-8px' : '0', zIndex: displayCases.length - i }}
                  >
                    <div 
                      className="w-10 h-10 rounded-full border-2 overflow-hidden transition-transform group-hover:scale-105"
                      style={{ borderColor: `${color}60`, backgroundColor: `${color}20` }}
                    >
                      <WorkerPhoto
                        worker={c}
                        alt={c.workerName}
                        className="w-full h-full object-cover"
                        fallback={
                          <div className="w-full h-full flex items-center justify-center text-lg">
                            {(c.nationality || '').toUpperCase().includes('PHILIPP') ? '🇵🇭' :
                             (c.nationality || '').toUpperCase().includes('INDO') ? '🇮🇩' :
                             (c.nationality || '').toUpperCase().includes('EGYPT') ? '🇪🇬' :
                             (c.nationality || '').toUpperCase().includes('INDIA') ? '🇮🇳' : '👤'}
                          </div>
                        }
                      />
                    </div>
                  </div>
                ))}
                {count > 4 && (
                  <div 
                    className="w-10 h-10 rounded-full border-2 flex items-center justify-center text-xs font-bold"
                    style={{ borderColor: `${color}60`, backgroundColor: `${color}20`, marginLeft: '-8px', color: color }}
                  >
                    +{count - 4}
                  </div>
                )}
              </div>
              
              {/* Names List */}
              <div className="space-y-1 mt-2">
                {displayCases.slice(0, 2).map((c, i) => (
                  <div key={c.id || i} className="flex items-center justify-between text-xs">
                    <span className="text-white/80 truncate max-w-[100px]">{c.workerName}</span>
                    <span className="text-slate-500">{c.nationality?.slice(0, 3)}</span>
                  </div>
                ))}
              </div>
              
              {/* Click hint */}
              <p className="text-center text-[10px] mt-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: color }}>
                Click to view all →
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Warranty Alerts Card - Premium Design
const WarrantyAlertsCard = ({ warrantyStats, onOpenWarrantyModal }) => {
  const alerts = warrantyStats?.warranty_alerts || [];
  const criticalCount = warrantyStats?.warranty_critical || 0;
  const warningCount = warrantyStats?.warranty_warning || 0;
  const activeCount = warrantyStats?.warranty_active || 0;
  const totalAlerts = criticalCount + warningCount;
  
  const getStatusColor = (status) => {
    if (status === 'critical') return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' };
    if (status === 'warning') return { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' };
    return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' };
  };
  
  return (
    <motion.div variants={itemVariants}>
      <Card 
        className="bg-gradient-to-br from-violet-500/10 via-slate-800/60 to-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden h-full min-h-[220px] hover:border-violet-500/30 transition-all duration-300 cursor-pointer group relative"
        onClick={() => onOpenWarrantyModal && onOpenWarrantyModal(warrantyStats)}
        data-testid="warranty-alerts-card"
      >
        {/* Top gradient border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500" />
        
        {/* Background shield icon */}
        <div className="absolute right-4 top-4 opacity-10">
          <Shield className="h-24 w-24 text-violet-400" />
        </div>
        
        <CardContent className="p-5 h-full flex flex-col relative z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                <ShieldAlert className="h-5 w-5 text-violet-400" />
              </div>
              <div>
                <span className="text-white font-semibold text-sm">Warranty Alerts</span>
                <span className="text-slate-500 text-xs block">تنبيهات الضمان</span>
              </div>
            </div>
            {totalAlerts > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/20 animate-pulse">
                <span className="text-lg font-black text-red-400">{totalAlerts}</span>
              </div>
            )}
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {/* Critical */}
            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-2 text-center">
              <ShieldX className="h-4 w-4 text-red-400 mx-auto mb-1" />
              <p className="text-xl font-black text-red-400">{criticalCount}</p>
              <p className="text-[9px] text-red-300/70 font-medium">CRITICAL</p>
            </div>
            
            {/* Warning */}
            <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-2 text-center">
              <ShieldAlert className="h-4 w-4 text-amber-400 mx-auto mb-1" />
              <p className="text-xl font-black text-amber-400">{warningCount}</p>
              <p className="text-[9px] text-amber-300/70 font-medium">WARNING</p>
            </div>
            
            {/* Active */}
            <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-2 text-center">
              <ShieldCheck className="h-4 w-4 text-emerald-400 mx-auto mb-1" />
              <p className="text-xl font-black text-emerald-400">{activeCount}</p>
              <p className="text-[9px] text-emerald-300/70 font-medium">ACTIVE</p>
            </div>
          </div>
          
          {/* Alert List (Top 2) */}
          {alerts.length > 0 ? (
            <div className="flex-1 space-y-2">
              {alerts.slice(0, 2).map((alert, i) => {
                const colors = getStatusColor(alert.status);
                return (
                  <div 
                    key={alert.id || i} 
                    className={`flex items-center justify-between p-2 rounded-lg ${colors.bg} border ${colors.border}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${colors.text}`}>
                        {alert.workerName?.substring(0, 15)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className={`h-3 w-3 ${colors.text}`} />
                      <span className={`text-xs font-bold ${colors.text}`}>
                        {alert.daysRemaining} days
                      </span>
                    </div>
                  </div>
                );
              })}
              {alerts.length > 2 && (
                <p className="text-center text-[10px] text-violet-400">
                  +{alerts.length - 2} more alerts
                </p>
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShieldCheck className="h-8 w-8 text-emerald-500/50 mx-auto mb-1" />
                <p className="text-slate-500 text-xs">All warranties OK</p>
              </div>
            </div>
          )}
          
          {/* Click hint */}
          <p className="text-center text-[10px] mt-2 text-violet-400 opacity-0 group-hover:opacity-100 transition-opacity">
            Click for warranty details →
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Warranty Details Modal
const WarrantyDetailsModal = ({ isOpen, onClose, warrantyStats }) => {
  if (!isOpen) return null;
  
  const alerts = warrantyStats?.warranty_alerts || [];
  
  const getStatusColor = (status) => {
    if (status === 'critical') return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', label: 'CRITICAL' };
    if (status === 'warning') return { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', label: 'WARNING' };
    return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'ACTIVE' };
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900/95 backdrop-blur-2xl border border-white/10 rounded-2xl max-w-3xl max-h-[85vh] overflow-hidden p-0">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-violet-600/20 via-purple-600/20 to-pink-600/20 p-6 border-b border-white/10">
          <DialogTitle className="flex items-center gap-4 relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-violet-500/30 border border-violet-500/50 flex items-center justify-center">
              <Shield className="h-7 w-7 text-violet-400" />
            </div>
            <div>
              <span className="text-white font-bold text-2xl">Warranty Tracking</span>
              <span className="text-slate-400 text-sm block">متابعة الضمانات</span>
            </div>
            <div className="ml-auto flex items-center gap-4">
              <div className="text-center px-3 py-1 rounded-lg bg-red-500/20 border border-red-500/30">
                <span className="text-xl font-black text-red-400">{warrantyStats?.warranty_critical || 0}</span>
                <span className="text-[10px] text-red-300 block">Critical</span>
              </div>
              <div className="text-center px-3 py-1 rounded-lg bg-amber-500/20 border border-amber-500/30">
                <span className="text-xl font-black text-amber-400">{warrantyStats?.warranty_warning || 0}</span>
                <span className="text-[10px] text-amber-300 block">Warning</span>
              </div>
              <div className="text-center px-3 py-1 rounded-lg bg-emerald-500/20 border border-emerald-500/30">
                <span className="text-xl font-black text-emerald-400">{warrantyStats?.warranty_active || 0}</span>
                <span className="text-[10px] text-emerald-300 block">Active</span>
              </div>
            </div>
          </DialogTitle>
        </div>
        
        <div className="overflow-y-auto max-h-[60vh] p-4 space-y-3">
          {alerts.length === 0 ? (
            <div className="text-center py-16">
              <ShieldCheck className="h-20 w-20 mx-auto mb-4 text-emerald-500/50" />
              <p className="text-slate-500 text-lg">No warranty alerts</p>
              <p className="text-slate-600 text-sm">All warranties are in good standing</p>
            </div>
          ) : (
            alerts.map((alert, i) => {
              const colors = getStatusColor(alert.status);
              
              return (
                <motion.div
                  key={alert.id || i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`relative bg-white/5 border ${colors.border} rounded-xl p-4 hover:bg-white/10 transition-all`}
                >
                  <div className="flex items-center gap-4">
                    {/* Worker Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-bold">{alert.workerName}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>
                          {colors.label}
                        </span>
                      </div>
                      <p className="text-slate-500 text-xs">{alert.nationality}</p>
                    </div>
                    
                    {/* Days Remaining */}
                    <div className="text-center">
                      <div className={`text-3xl font-black ${colors.text}`}>{alert.daysRemaining}</div>
                      <p className="text-slate-500 text-xs">Days Left</p>
                    </div>
                    
                    {/* Expiry Date */}
                    <div className="text-center">
                      <div className="text-white font-semibold">{alert.expiryDate}</div>
                      <p className="text-slate-500 text-xs">Expiry Date</p>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mt-3 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${alert.status === 'critical' ? 'bg-red-500' : alert.status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500'} transition-all duration-500`}
                      style={{ width: `${Math.max(5, (alert.daysRemaining / 90) * 100)}%` }}
                    />
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Premium Flight Status Card - With Animated SVG Airplane
const FlightStatusCard = ({ tickets, onOpenFlightModal }) => {
  const activeFlights = tickets?.filter(t => t.flightStatus?.status === 'active' || t.flightStatus?.status === 'in_flight') || [];
  const landedFlights = tickets?.filter(t => t.flightStatus?.status === 'landed') || [];
  const scheduledFlights = tickets?.filter(t => !t.flightStatus?.status || t.flightStatus?.status === 'scheduled') || [];
  const allFlights = tickets || [];
  
  return (
    <motion.div variants={itemVariants}>
      <Card 
        className="bg-gradient-to-br from-sky-500/10 via-slate-800/60 to-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden h-full min-h-[220px] hover:border-sky-500/30 transition-all duration-300 cursor-pointer group relative"
        onClick={() => onOpenFlightModal && onOpenFlightModal(allFlights)}
      >
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-sky-900/30 via-slate-900/50 to-slate-900/80" />
        
        {/* Animated airplane with flight path */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Flight path SVG */}
          <svg className="w-full h-full opacity-40" viewBox="0 0 300 180">
            <defs>
              <linearGradient id="flightPath" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#38bdf8', stopOpacity: 0.3 }} />
                <stop offset="50%" style={{ stopColor: '#38bdf8', stopOpacity: 0.8 }} />
                <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 0.3 }} />
              </linearGradient>
            </defs>
            {/* Curved flight path */}
            <path 
              d="M 30 140 Q 100 40 150 90 Q 200 140 270 60" 
              fill="none" 
              stroke="url(#flightPath)" 
              strokeWidth="2" 
              strokeDasharray="6 4"
            />
            {/* Departure airport marker */}
            <circle cx="30" cy="140" r="8" fill="#38bdf8" opacity="0.7">
              <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" />
            </circle>
            {/* Arrival airport marker */}
            <circle cx="270" cy="60" r="8" fill="#10b981" opacity="0.7">
              <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" />
            </circle>
          </svg>
          
          {/* Animated Airplane SVG - Realistic Style - LARGER */}
          <motion.div
            className="absolute"
            initial={{ left: '0%', top: '65%' }}
            animate={{ 
              left: ['0%', '35%', '70%'], 
              top: ['65%', '30%', '15%'],
            }}
            transition={{ 
              duration: 8, 
              repeat: Infinity, 
              ease: "easeInOut",
              repeatType: "loop"
            }}
          >
            <svg 
              width="100" 
              height="100" 
              viewBox="0 0 100 100" 
              className="drop-shadow-[0_0_25px_rgba(56,189,248,0.9)]"
              style={{ transform: 'rotate(-25deg)' }}
            >
              {/* Airplane body */}
              <defs>
                <linearGradient id="planeBody" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#ffffff' }} />
                  <stop offset="50%" style={{ stopColor: '#e2e8f0' }} />
                  <stop offset="100%" style={{ stopColor: '#94a3b8' }} />
                </linearGradient>
                <linearGradient id="planeWing" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: '#cbd5e1' }} />
                  <stop offset="50%" style={{ stopColor: '#94a3b8' }} />
                  <stop offset="100%" style={{ stopColor: '#64748b' }} />
                </linearGradient>
                <linearGradient id="wingShine" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#ffffff', stopOpacity: 0.5 }} />
                  <stop offset="100%" style={{ stopColor: '#ffffff', stopOpacity: 0 }} />
                </linearGradient>
              </defs>
              
              {/* Main Fuselage */}
              <ellipse cx="50" cy="50" rx="42" ry="8" fill="url(#planeBody)" />
              
              {/* Cockpit */}
              <ellipse cx="88" cy="50" rx="8" ry="6" fill="#0c4a6e" opacity="0.8" />
              <ellipse cx="88" cy="50" rx="6" ry="4" fill="#38bdf8" opacity="0.4" />
              
              {/* Top Wing - LONGER */}
              <path d="M 40 50 L 25 15 L 32 15 L 55 50 Z" fill="url(#planeWing)" />
              <path d="M 40 50 L 25 15 L 32 15 L 55 50 Z" fill="url(#wingShine)" />
              
              {/* Bottom Wing - LONGER */}
              <path d="M 40 50 L 25 85 L 32 85 L 55 50 Z" fill="url(#planeWing)" />
              
              {/* Wing Tips */}
              <path d="M 25 15 L 22 12 L 28 15 Z" fill="#38bdf8" />
              <path d="M 25 85 L 22 88 L 28 85 Z" fill="#38bdf8" />
              
              {/* Tail Wings */}
              <path d="M 12 50 L 5 38 L 10 40 L 18 50 Z" fill="#64748b" />
              <path d="M 12 50 L 5 62 L 10 60 L 18 50 Z" fill="#64748b" />
              
              {/* Vertical Tail Fin */}
              <path d="M 8 50 L 2 32 L 8 42 Z" fill="#0ea5e9" />
              
              {/* Windows */}
              <circle cx="60" cy="48" r="2" fill="#0ea5e9" />
              <circle cx="67" cy="48" r="2" fill="#0ea5e9" />
              <circle cx="74" cy="48" r="2" fill="#0ea5e9" />
              <circle cx="81" cy="48" r="2" fill="#0ea5e9" />
              
              {/* Engine Glow Effect */}
              <ellipse cx="5" cy="50" rx="5" ry="3" fill="#38bdf8" opacity="0.7">
                <animate attributeName="opacity" values="0.4;0.9;0.4" dur="0.3s" repeatCount="indefinite" />
                <animate attributeName="rx" values="4;6;4" dur="0.3s" repeatCount="indefinite" />
              </ellipse>
              
              {/* Fuselage Shine */}
              <ellipse cx="50" cy="46" rx="35" ry="3" fill="white" opacity="0.2" />
            </svg>
          </motion.div>
        </div>
        
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-500 via-cyan-400 to-blue-500" />
        <CardContent className="p-5 h-full flex flex-col relative z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-sky-500/30 border border-sky-500/50 flex items-center justify-center">
                <Plane className="h-5 w-5 text-sky-300" />
              </div>
              <div>
                <span className="text-white font-bold text-base">Flight Status</span>
                <span className="text-sky-300/70 text-xs block">حالة الرحلات</span>
              </div>
            </div>
            <div className="px-3 py-1.5 rounded-xl bg-sky-500/20 border border-sky-500/30">
              <span className="text-2xl font-black text-sky-400">{allFlights.length}</span>
            </div>
          </div>
          
          {/* Flight Stats */}
          <div className="flex-1 grid grid-cols-3 gap-2 mt-2">
            {/* In Flight */}
            <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-3 text-center backdrop-blur-sm">
              <motion.div
                animate={{ y: [0, -3, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-2xl mb-1"
              >
                ✈️
              </motion.div>
              <p className="text-2xl font-black text-blue-400">{activeFlights.length}</p>
              <p className="text-[10px] text-blue-300/70 font-medium">IN FLIGHT</p>
            </div>
            
            {/* Landed */}
            <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-3 text-center backdrop-blur-sm">
              <div className="text-2xl mb-1">🛬</div>
              <p className="text-2xl font-black text-emerald-400">{landedFlights.length}</p>
              <p className="text-[10px] text-emerald-300/70 font-medium">LANDED</p>
            </div>
            
            {/* Scheduled */}
            <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-3 text-center backdrop-blur-sm">
              <div className="text-2xl mb-1">📅</div>
              <p className="text-2xl font-black text-amber-400">{scheduledFlights.length}</p>
              <p className="text-[10px] text-amber-300/70 font-medium">SCHEDULED</p>
            </div>
          </div>
          
          {/* Active Flight Info */}
          {activeFlights.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                <span className="text-sm font-bold text-white">{activeFlights[0].flightNumber}</span>
              </div>
              <span className="text-xs text-sky-400">{activeFlights[0].departure} → {activeFlights[0].arrival}</span>
            </div>
          )}
          
          {/* Click hint */}
          <p className="text-center text-[10px] mt-2 text-sky-400 opacity-0 group-hover:opacity-100 transition-opacity">
            Click for flight details →
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Flight Details Modal
const FlightDetailsModal = ({ isOpen, onClose, tickets }) => {
  if (!isOpen) return null;
  
  const getStatusColor = (status) => {
    if (status === 'landed') return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' };
    if (status === 'active' || status === 'in_flight') return { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' };
    return { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' };
  };
  
  const getStatusLabel = (status) => {
    if (status === 'landed') return 'LANDED';
    if (status === 'active' || status === 'in_flight') return 'IN FLIGHT';
    return 'SCHEDULED';
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900/95 backdrop-blur-2xl border border-white/10 rounded-2xl max-w-3xl max-h-[85vh] overflow-hidden p-0">
        {/* Header with plane animation */}
        <div className="relative bg-gradient-to-r from-sky-600/20 via-blue-600/20 to-indigo-600/20 p-6 border-b border-white/10">
          <div className="absolute inset-0 overflow-hidden">
            <svg className="w-full h-full opacity-20" viewBox="0 0 400 100">
              <path d="M 0 50 Q 100 10 200 50 Q 300 90 400 50" fill="none" stroke="#38bdf8" strokeWidth="2" strokeDasharray="8 4" />
            </svg>
          </div>
          <DialogTitle className="flex items-center gap-4 relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-sky-500/30 border border-sky-500/50 flex items-center justify-center">
              <Plane className="h-7 w-7 text-sky-400" />
            </div>
            <div>
              <span className="text-white font-bold text-2xl">Flight Board</span>
              <span className="text-slate-400 text-sm block">لوحة الرحلات الجوية</span>
            </div>
            <div className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10">
              <span className="text-3xl font-black text-sky-400">{tickets?.length || 0}</span>
              <span className="text-slate-500 text-sm">Flights</span>
            </div>
          </DialogTitle>
        </div>
        
        <div className="overflow-y-auto max-h-[60vh] p-4 space-y-3">
          {tickets?.length === 0 ? (
            <div className="text-center py-16">
              <Plane className="h-20 w-20 mx-auto mb-4 text-slate-700" />
              <p className="text-slate-500 text-lg">No flights found</p>
            </div>
          ) : (
            tickets?.map((ticket, i) => {
              const status = ticket.flightStatus?.status || 'scheduled';
              const colors = getStatusColor(status);
              
              return (
                <motion.div
                  key={ticket.id || i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`relative bg-white/5 border ${colors.border} rounded-xl p-4 hover:bg-white/10 transition-all overflow-hidden`}
                >
                  {/* Background plane path */}
                  <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-10">
                    <svg className="w-full h-full" viewBox="0 0 100 60">
                      <path d="M 0 30 Q 50 10 100 30" fill="none" stroke="currentColor" strokeWidth="1" className={colors.text} />
                    </svg>
                  </div>
                  
                  <div className="relative z-10 flex items-center gap-4">
                    {/* Flight Number */}
                    <div className="text-center min-w-[80px]">
                      <p className={`text-xl font-black ${colors.text}`}>{ticket.flightNumber || 'N/A'}</p>
                      <p className="text-[10px] text-slate-500 uppercase">Flight</p>
                    </div>
                    
                    {/* Route */}
                    <div className="flex-1 flex items-center gap-3">
                      {/* Departure */}
                      <div className="text-center">
                        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-1">
                          <PlaneTakeoff className="h-5 w-5 text-slate-400" />
                        </div>
                        <p className="text-white font-bold">{ticket.departure || '---'}</p>
                        <p className="text-[10px] text-slate-500">{ticket.departureTime || '--:--'}</p>
                      </div>
                      
                      {/* Flight Path */}
                      <div className="flex-1 flex items-center gap-2 px-2">
                        <div className="h-[2px] flex-1 bg-gradient-to-r from-slate-600 via-slate-500 to-slate-600 rounded relative">
                          <motion.div
                            className="absolute top-1/2 -translate-y-1/2"
                            animate={{ left: ['0%', '100%'] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                          >
                            <Plane className={`h-4 w-4 ${colors.text}`} />
                          </motion.div>
                        </div>
                      </div>
                      
                      {/* Arrival */}
                      <div className="text-center">
                        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-1">
                          <PlaneLanding className="h-5 w-5 text-slate-400" />
                        </div>
                        <p className="text-white font-bold">{ticket.arrival || '---'}</p>
                        <p className="text-[10px] text-slate-500">{ticket.arrivalTime || '--:--'}</p>
                      </div>
                    </div>
                    
                    {/* Status */}
                    <div className={`px-4 py-2 rounded-xl ${colors.bg} border ${colors.border} text-center min-w-[100px]`}>
                      <p className={`text-sm font-bold ${colors.text}`}>{getStatusLabel(status)}</p>
                      {ticket.flightStatus?.progressPercent && (
                        <p className="text-[10px] text-slate-500">{Math.round(ticket.flightStatus.progressPercent)}%</p>
                      )}
                    </div>
                    
                    {/* Date */}
                    <div className="text-center min-w-[80px]">
                      <Calendar className="h-4 w-4 mx-auto text-slate-500 mb-1" />
                      <p className="text-xs text-slate-400">{ticket.departureDate || 'N/A'}</p>
                    </div>
                  </div>
                  
                  {/* Passenger info if available */}
                  {ticket.passengerName && (
                    <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2">
                      <Users className="h-3 w-3 text-slate-500" />
                      <span className="text-xs text-slate-400">{ticket.passengerName}</span>
                    </div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const navigate = useNavigate();
  const { 
    suppliers, 
    cases,
    loading, 
    getDashboardStats,
    fetchSuppliers,
    fetchCases,
    refreshAll 
  } = useApp();
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [warrantyStats, setWarrantyStats] = useState(null);
  
  // Modal states
  const [detailsModal, setDetailsModal] = useState({ isOpen: false, data: null });
  const [flightModal, setFlightModal] = useState({ isOpen: false, tickets: [] });
  const [warrantyModal, setWarrantyModal] = useState({ isOpen: false, data: null });
  
  // Get stats from context
  const stats = getDashboardStats();
  
  // Financial data from stats
  const financials = {
    totalPaid: stats.totalPaid || 0,
    totalRemaining: stats.totalRemaining || 0,
    totalCost: stats.totalCost || 0,
    totalDeductions: stats.totalDeductions || 0
  };

  // Fetch data on mount
  useEffect(() => {
    fetchSuppliers();
    fetchCases();
    
    // Fetch flight tickets
    apiGet('/api/tickets')
      .then(data => setTickets(data))
      .catch(err => console.error('Failed to fetch tickets:', err));
    
    // Fetch warranty stats
    apiGet('/api/cases/warranty/stats')
      .then(data => setWarrantyStats(data))
      .catch(err => console.error('Failed to fetch warranty stats:', err));
  }, [fetchSuppliers, fetchCases]);
  
  // Modal handlers
  const openDetailsModal = (data) => {
    setDetailsModal({ isOpen: true, data });
  };
  
  const closeDetailsModal = () => {
    setDetailsModal({ isOpen: false, data: null });
  };
  
  const openFlightModal = (ticketsList) => {
    setFlightModal({ isOpen: true, tickets: ticketsList });
  };
  
  const closeFlightModal = () => {
    setFlightModal({ isOpen: false, tickets: [] });
  };
  
  const openWarrantyModal = (data) => {
    setWarrantyModal({ isOpen: true, data });
  };
  
  const closeWarrantyModal = () => {
    setWarrantyModal({ isOpen: false, data: null });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshAll();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  // Filter cases by status - Using 'status' field as PRIMARY source
  // Status values: abroad, arrived, runaway, settled, noarrival
  const runawayCase = cases?.filter(c => {
    const status = (c.status || '').toLowerCase();
    return status === 'runaway';
  }) || [];
  
  const abroadCases = cases?.filter(c => {
    const status = (c.status || '').toLowerCase();
    return status === 'abroad';
  }) || [];
  
  const settledCases = cases?.filter(c => {
    const status = (c.status || '').toLowerCase();
    return status === 'settled';
  }) || [];
  
  const arrivedCases = cases?.filter(c => {
    const status = (c.status || '').toLowerCase();
    return status === 'arrived';
  }) || [];

  // Calculate percentages for charts
  const totalWorkers = stats?.totalWorkers || 0;
  const arrivedPercent = totalWorkers > 0 ? Math.round((stats?.arrived || 0) / totalWorkers * 100) : 0;
  const completionPercent = totalWorkers > 0 ? Math.round(((stats?.arrived || 0) + (stats?.settled || 0)) / totalWorkers * 100) : 0;

  // Check if still loading
  const isLoading = loading.suppliers || loading.cases;

  if (isLoading && suppliers.length === 0) {
    return <DashboardLoading />;
  }

  return (
    <div className="relative min-h-screen" data-testid="dashboard-page">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-10 left-10 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="relative z-10 space-y-5"
      >
        {/* Header with Title and Globe */}
        <motion.div variants={itemVariants} className="relative mb-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white">
                Dashboard
              </h1>
              <p className="text-slate-500 text-sm mt-1">Workforce Management Overview</p>
            </div>
            <div className="hidden lg:block relative w-48 h-48 -mt-4 -mr-4">
              <Globe3D />
            </div>
          </div>
        </motion.div>

        {/* Main Grid - Top Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Workers Card - Orange */}
          <HexStatCard
            hexColor="#F97316"
            title="STATISTICS"
            subtitle="Total Workers"
            value={stats?.totalWorkers || 0}
            icon={Users}
          />
          
          {/* Workers Abroad Card - With Photos */}
          <DynamicStatusCard
            title="Workers Abroad"
            titleAr="العمال بالخارج"
            cases={abroadCases}
            color="#22D3EE"
            icon={Plane}
            emptyMessage="No workers abroad"
            bgGradient="from-cyan-500/10 via-slate-800/60 to-slate-900/80"
            onOpenModal={openDetailsModal}
          />
          
          {/* Flight Status Card */}
          <FlightStatusCard tickets={tickets} onOpenFlightModal={openFlightModal} />
          
          {/* Warranty Alerts Card */}
          <WarrantyAlertsCard warrantyStats={warrantyStats} onOpenWarrantyModal={openWarrantyModal} />
        </div>

        {/* Second Row - Dynamic Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Arrived Workers Card - With Photos */}
          <DynamicStatusCard
            title="Arrived Workers"
            titleAr="العمال الواصلين"
            cases={arrivedCases}
            color="#10B981"
            icon={UserCheck}
            emptyMessage="No arrived workers"
            bgGradient="from-emerald-500/10 via-slate-800/60 to-slate-900/80"
            onOpenModal={openDetailsModal}
          />
          
          {/* Runaway Workers Card - Red - With Photos */}
          <DynamicStatusCard
            title="Runaway Cases"
            titleAr="حالات الهروب"
            cases={runawayCase}
            color="#EF4444"
            icon={AlertTriangle}
            emptyMessage="No runaway cases"
            bgGradient="from-rose-500/10 via-slate-800/60 to-slate-900/80"
            onOpenModal={openDetailsModal}
          />
          
          {/* Settled Cases Card - Yellow - With Photos */}
          <DynamicStatusCard
            title="Settled Cases"
            titleAr="حالات التسوية"
            cases={settledCases}
            color="#EAB308"
            icon={CheckCircle}
            emptyMessage="No settled cases"
            bgGradient="from-amber-500/10 via-slate-800/60 to-slate-900/80"
            onOpenModal={openDetailsModal}
          />
          
          {/* Workers Activity Line Chart - Spans 2 columns */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden h-full">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-amber-500" />
              <CardContent className="p-5">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-white font-semibold">Workers Activity</span>
                  <div className="flex items-center gap-3 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-cyan-400" />
                      <span className="text-slate-500">Arrivals</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-amber-400" />
                      <span className="text-slate-500">Departures</span>
                    </div>
                  </div>
                </div>
                <div className="h-28">
                  <AnimatedLineChart height={100} />
                </div>
                <div className="flex justify-between mt-2 text-[10px] text-slate-600">
                  <span>Jan</span>
                  <span>Feb</span>
                  <span>Mar</span>
                  <span>Apr</span>
                  <span>May</span>
                  <span>Jun</span>
                  <span>Jul</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Third Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Completion Rate - Donut Chart */}
          <motion.div variants={itemVariants}>
            <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden h-full">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-amber-500" />
              <CardContent className="p-5">
                <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">Completion Rate</span>
                <div className="flex items-center gap-4 mt-3">
                  <DonutChart 
                    percentage={completionPercent || 50} 
                    color="#F97316"
                    size={90}
                  />
                  <div className="space-y-1 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                      <span className="text-slate-400">Active</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                      <span className="text-slate-400">Abroad</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span className="text-slate-400">Settled</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                      <span className="text-slate-400">Pending</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          {/* Performance Indicator - Bar Chart */}
          <motion.div variants={itemVariants}>
            <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden h-full">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-blue-500" />
              <CardContent className="p-5">
                <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">Performance</span>
                <div className="mt-3">
                  <AnimatedBarChart color="#22d3ee" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          {/* Stats Summary */}
          <motion.div variants={itemVariants}>
            <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden h-full">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
              <CardContent className="p-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-4xl font-black text-white">{arrivedPercent}%</span>
                    <p className="text-slate-500 text-xs mt-1">Arrived Rate</p>
                  </div>
                  <div>
                    <span className="text-4xl font-black text-cyan-400">{completionPercent}%</span>
                    <p className="text-slate-500 text-xs mt-1">Completion</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-white/5">
                  <span className="text-slate-500 text-xs">Overall Status</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          {/* Financial Summary Card */}
          <motion.div variants={itemVariants}>
            <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden h-full">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500" />
              <CardContent className="p-5">
                <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">Financial</span>
                <div className="mt-3">
                  <div className="text-2xl font-black text-white">
                    {financials.totalPaid.toLocaleString()}
                    <span className="text-sm font-normal text-slate-500 ml-1">AED</span>
                  </div>
                  <p className="text-slate-500 text-xs mt-1">Total Paid</p>
                </div>
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="text-lg font-bold text-orange-400">
                    {financials.totalRemaining.toLocaleString()}
                    <span className="text-xs font-normal text-slate-500 ml-1">AED</span>
                  </div>
                  <p className="text-slate-500 text-xs">Remaining</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Quick Actions Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button
            onClick={() => navigate('/suppliers')}
            className="bg-slate-900/70 backdrop-blur-xl border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 rounded-xl h-auto py-4 flex flex-col items-center gap-2"
          >
            <Users className="h-6 w-6" />
            <span className="text-xs">Suppliers</span>
          </Button>
          <Button
            onClick={() => navigate('/visa-center')}
            className="bg-slate-900/70 backdrop-blur-xl border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 rounded-xl h-auto py-4 flex flex-col items-center gap-2"
          >
            <CheckCircle className="h-6 w-6" />
            <span className="text-xs">Visas</span>
          </Button>
          <Button
            onClick={() => navigate('/passport-tracker')}
            className="bg-slate-900/70 backdrop-blur-xl border border-purple-500/30 text-purple-400 hover:bg-purple-500/10 rounded-xl h-auto py-4 flex flex-col items-center gap-2"
          >
            <Globe className="h-6 w-6" />
            <span className="text-xs">Passports</span>
          </Button>
          <Button
            onClick={() => navigate('/flight-scanner')}
            className="bg-slate-900/70 backdrop-blur-xl border border-orange-500/30 text-orange-400 hover:bg-orange-500/10 rounded-xl h-auto py-4 flex flex-col items-center gap-2"
          >
            <Plane className="h-6 w-6" />
            <span className="text-xs">Flights</span>
          </Button>
        </div>
      </motion.div>
      
      {/* Details Modal */}
      {detailsModal.data && (
        <DetailsModal
          isOpen={detailsModal.isOpen}
          onClose={closeDetailsModal}
          title={detailsModal.data.title}
          titleAr={detailsModal.data.titleAr}
          cases={detailsModal.data.cases}
          color={detailsModal.data.color}
          icon={detailsModal.data.icon}
        />
      )}
      
      {/* Flight Details Modal */}
      <FlightDetailsModal
        isOpen={flightModal.isOpen}
        onClose={closeFlightModal}
        tickets={flightModal.tickets}
      />
      
      {/* Warranty Details Modal */}
      <WarrantyDetailsModal
        isOpen={warrantyModal.isOpen}
        onClose={closeWarrantyModal}
        warrantyStats={warrantyModal.data}
      />
    </div>
  );
};

export default Dashboard;
